import { useEffect, useRef, useState } from "react"
import _ from "lodash"
import moment from "moment"

import { postSubRegions } from "helpers/backend_helper"

import { ordenarRegiones } from "../helpers"
import { SAVED_FEEDBACK_MS } from "../static"

/**
 * Núcleo de la pantalla de alta de disponibilidad, compartido por el portal del
 * proveedor y el del distribuidor. Concentra todo lo que es igual en ambos
 * —fechas, sucursales, productos, pestañas, zonas, registros, expandido y
 * edición— y recibe inyectada la capa de API, que es lo único que cambia.
 *
 * @param {function} load     async () => ({ dates, dailies, farms })
 * @param {function} create   async (registro, { date }) => respuesta
 * @param {function} update   async (registro, { date }) => respuesta
 * @param {boolean}  ready    si es false no se carga nada todavía
 */
export default function useAvailabilityBoard({ load, create, update, ready = true }) {
  const [isLoading, setIsLoading] = useState(true)

  const [dates, setDates] = useState([])
  const [date, setDate] = useState(null)

  const [farms, setFarms] = useState([])
  const [farmId, setFarmId] = useState(null)

  const [products, setProducts] = useState([])
  const [tabActive, setTabActive] = useState(0)

  const [regions, setRegions] = useState([])
  const [dailies, setDailies] = useState([])

  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const savedTimer = useRef(null)

  const [expanded, setExpanded] = useState(null)
  const [editing, setEditing] = useState(null)

  useEffect(() => () => clearTimeout(savedTimer.current), [])

  const seleccionarFarm = farm => {
    setFarmId(farm ? farm.id : null)
    setRegions(farm ? ordenarRegiones(farm.regions) : [])
    setProducts(farm ? _.orderBy(farm.products || [], ["position"], ["asc"]) : [])
    setTabActive(0)
    setExpanded(null)
    setEditing(null)
  }

  // `load` viene memoizada por quien la inyecta: al cambiar aquello de lo que
  // depende (el proveedor elegido, por ejemplo) cambia su identidad y recarga.
  useEffect(() => {
    if (!ready) return undefined

    let cancelado = false
    const cargar = async () => {
      setIsLoading(true)
      try {
        const respuesta = (await load()) || {}
        if (cancelado) return

        const listaFechas = respuesta.dates || []
        const hoy = moment().format("YYYY-MM-DD")
        setDates(listaFechas)
        setDate(listaFechas.includes(hoy) ? hoy : listaFechas[0] || null)
        setDailies(respuesta.dailies || [])

        const listaFarms = respuesta.farms || []
        setFarms(listaFarms)
        seleccionarFarm(listaFarms[0] || null)
      } catch (error) {
        if (cancelado) return
        setDates([])
        setDailies([])
        setFarms([])
        seleccionarFarm(null)
      }
      if (!cancelado) setIsLoading(false)
    }

    cargar()
    return () => {
      cancelado = true
    }
    // eslint-disable-next-line
  }, [load, ready])

  const farm = farms.find(item => item.id === farmId) || null
  const product = products[tabActive] || null

  const registradas = dailies.filter(
    daily =>
      product &&
      Number(daily.farm_id) === Number(farmId) &&
      Number(daily.product_id) === Number(product.id) &&
      daily.date === date
  )

  const clearSelection = () => {
    setExpanded(null)
    setEditing(null)
  }

  const onChangeDate = value => {
    setDate(value)
    clearSelection()
  }

  const onChangeFarm = value =>
    seleccionarFarm(farms.find(item => item.id === value) || null)

  const onChangeTab = index => {
    setTabActive(index)
    clearSelection()
  }

  const onToggleExpanded = id =>
    setExpanded(previo => (previo === id ? null : id))

  const onStartEdit = id => {
    setEditing(id)
    setExpanded(id)
  }

  const onCancelEdit = () => setEditing(null)

  const marcarGuardado = () => {
    setIsSaved(true)
    clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setIsSaved(false), SAVED_FEEDBACK_MS)
  }

  /** Mezcla en la lista los registros devueltos por el servidor. */
  const absorber = response => {
    const devueltos = Array.isArray(response) ? response : [response]
    setDailies(previo => {
      const siguiente = [...previo]
      devueltos.forEach(item => {
        const index = siguiente.findIndex(daily => daily.id === item.id)
        if (index >= 0) siguiente[index] = item
        else siguiente.push(item)
      })
      return siguiente
    })
  }

  /** Alta. Devuelve true si el servidor aceptó el registro. */
  const onCreate = async registro => {
    if (isSaving) return false
    setIsSaving(true)
    let ok = false
    try {
      absorber(await create(registro, { date }))
      marcarGuardado()
      ok = true
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
    return ok
  }

  /** Edición inline. Devuelve true si el servidor aceptó los cambios. */
  const onUpdate = async registro => {
    if (isSaving) return false
    setIsSaving(true)
    let ok = false
    try {
      absorber(await update(registro, { date }))
      setEditing(null)
      ok = true
    } catch (error) {
      // api_helper ya notifica el error
    }
    setIsSaving(false)
    return ok
  }

  /**
   * Guarda las sub-zonas y refresca la sucursal. Devuelve la lista de zonas ya
   * actualizada para que quien llamó pueda emparejar los precios capturados
   * con el id definitivo de cada sub-zona nueva.
   */
  const onSaveZones = async subzonas => {
    if (!farm) return []
    const response = await postSubRegions({ regions: subzonas, farm })
    const actualizadas = response || []
    setRegions(ordenarRegiones(actualizadas))
    setFarms(previo =>
      previo.map(item =>
        item.id === farm.id ? { ...item, regions: actualizadas } : item
      )
    )
    return actualizadas
  }

  return {
    state: {
      isLoading,
      dates,
      date,
      farms,
      farmId,
      farm,
      products,
      tabActive,
      product,
      regions,
      registradas,
      isSaving,
      isSaved,
      expanded,
      editing,
    },
    events: {
      onChangeDate,
      onChangeFarm,
      onChangeTab,
      onToggleExpanded,
      onStartEdit,
      onCancelEdit,
      onCreate,
      onUpdate,
      onSaveZones,
    },
    clearSelection,
    setIsLoading,
  }
}
